import {
  AutomationsTab,
  BusinessAreasIcon,
  CustomPageContainer,
  ProcessesWithWorkflowsTab,
  TabItemProps,
  TablePaginationContextProvider,
} from '@/components';
import { LabelIcon } from '@/components/LabelIcon/LabelIcon';
import { PageDetailsActions } from '@/components/PageDetailsActions/PageDetailsActions';
import { TabContainer } from '@/components/TabContainer';
import { TabsWrapper } from '@/components/TabsWrapper';
import { HttpError } from '@/core/http';
import { RoutePath } from '@/core/router/route-paths';
import { AutomationModel } from '@/modules/Automations/domain';
import { getAutomationColumnsNamedMapped } from '@/modules/Automations/pages/AutomationsPage/utils';
import { useGetAutomationsByBusinessArea } from '@/modules/Automations/queries';
import { getProcessesColumnsNamedMapped } from '@/modules/Processes';
import { Process } from '@/modules/Processes/domain';
import { useGetProcessesByBusinessArea } from '@/modules/Processes/queries';
import { HttpStatusCode } from 'axios';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useDisableBusinessArea,
  useEnableBusinessArea,
  useGetBusinessArea,
} from '../../queries';
import { BusinessAreaEmployeesTab } from './components/BusinessAreaEmployeesTab/BusinessAreaEmployeesTab';
import { BusinessAreaHeaderRow } from './components/BusinessAreaHeaderRow';
import { BusinessAreaPerformanceCards } from './components/BusinessAreaPerformanceCards';

export const BusinessAreaDetailPage = (): ReactElement => {
  const { businessAreaId: paramBusinessAreaId } = useParams<{
    businessAreaId: string;
  }>();
  const businessAreaId = +paramBusinessAreaId!;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: businessArea,
    isLoading: isLoadingBusinessArea,
    isError,
    error,
  } = useGetBusinessArea(businessAreaId);

  useEffect(() => {
    if (isError && error) {
      if ((error as HttpError<unknown>)?.code === HttpStatusCode.NotFound) {
        navigate(RoutePath.notFound());
      } else {
        toast.error(t('error_get_business_area_details'));
      }
    }
  }, [isError, error, navigate, t]);

  const [status, setStatus] = useState<boolean>(false);

  useEffect(() => {
    setStatus(businessArea?.status === true);
  }, [businessArea]);

  const { mutate: enableBusinessArea } = useEnableBusinessArea();
  const { mutate: disableBusinessArea } = useDisableBusinessArea();

  const handleStatus = useCallback(
    (checked: boolean) => {
      if (!businessArea?.id) {
        return toast.error(t('generic_errors'));
      }
      setStatus(checked);
      if (checked) {
        enableBusinessArea(businessArea.id, {
          onError: () => {
            toast.error(t('error_enable_business_area'));
          },
          onSuccess: () => {
            toast.success(t('success_enable_business_area'));
          },
        });
      } else {
        disableBusinessArea(businessArea.id, {
          onError: () => {
            toast.error(t('error_disable_business_area'));
          },
          onSuccess: () => {
            toast.success(t('success_disable_business_area'));
          },
        });
      }
    },
    [businessArea, enableBusinessArea, disableBusinessArea, t],
  );

  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('employees'), badgeNumber: businessArea?.usersCount },
      {
        label: t('processes'),
        badgeNumber: businessArea?.processesCount,
      },
      { label: t('automations'), badgeNumber: businessArea?.automationsCount },
    ],
    [t, businessArea],
  );

  const routeChange = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const tabList = useMemo(() => {
    if (!businessAreaId || !businessArea) {
      return [];
    }
    return [
      {
        index: 0,
        tab: (
          <TablePaginationContextProvider>
            <BusinessAreaEmployeesTab businessAreaId={businessAreaId} />
          </TablePaginationContextProvider>
        ),
      },
      {
        index: 1,
        tab: (
          <TabContainer<Process>
            parentId={businessAreaId}
            useGetData={useGetProcessesByBusinessArea}
            getColumnsNamedMapped={getProcessesColumnsNamedMapped}
          >
            <ProcessesWithWorkflowsTab
              context="businessArea"
              canCreate
              businessAreaId={businessArea?.id}
            />
          </TabContainer>
        ),
      },
      {
        index: 4,
        tab: (
          <TabContainer<AutomationModel>
            parentId={businessAreaId}
            useGetData={useGetAutomationsByBusinessArea}
            getColumnsNamedMapped={getAutomationColumnsNamedMapped}
          >
            <AutomationsTab
              canCreate
              disableFields={['organizationId', 'companyId', 'businessAreaId']}
              businessAreaId={businessArea?.id}
              companyId={businessArea?.companyId}
              organizationId={businessArea?.organizationId}
            />
          </TabContainer>
        ),
      },
    ];
  }, [businessAreaId, businessArea]);

  const breadcrumbs = useMemo(() => {
    if (!businessArea) {
      return [];
    }
    return [
      {
        label: t('organizations'),
        href: RoutePath.organizations(),
      },
      {
        label: businessArea.organizationName,
        href: RoutePath.organizationDetail(
          businessArea.organizationId.toString(),
        ),
      },
      {
        label: businessArea.company,
        href: RoutePath.companyDetail(businessArea.companyId.toString()),
      },
      {
        label: businessArea.name,
      },
    ];
  }, [businessArea, t]);

  return (
    <CustomPageContainer>
      <PageDetailsActions onBack={routeChange} breadcrumbs={breadcrumbs}>
        <LabelIcon
          label={t('business_area')}
          icon={<BusinessAreasIcon className="size-5 text-blueNuit" />}
        />
      </PageDetailsActions>
      <BusinessAreaHeaderRow
        businessArea={businessArea}
        handleStatus={handleStatus}
        status={status}
        isLoading={isLoadingBusinessArea}
      />
      <TablePaginationContextProvider>
        <BusinessAreaPerformanceCards businessAreaId={businessAreaId} />
        <TabsWrapper tabs={tabs} tabList={tabList} />
      </TablePaginationContextProvider>
    </CustomPageContainer>
  );
};
